import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { first, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { GradeLesson } from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import {
  Session,
  SessionStatus,
} from '../../../../models/session.school.model';
import { FireTimestamp } from '../../../../models/timestamp.firestore.model';
import { UtilsService } from '../../../../services/utils.service';
import * as sessionActions from '../../../../state/actions/session.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeLessonsSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';

@Component({
  selector: 'app-dlg-upsert-session',
  standalone: true,
  imports: [ReactiveFormsModule, AsyncPipe, JsonPipe, BtnActionComponent],
  templateUrl: './dlg-upsert-session.component.html',
  styleUrl: './dlg-upsert-session.component.css',
})
export class DlgUpsertSessionComponent implements OnInit, OnDestroy {
  private store: Store<AppState> = inject(Store<AppState>);
  private actions$: Actions = inject(Actions);
  private logger: NGXLogger = inject(NGXLogger);
  utilsService: UtilsService = inject(UtilsService);
  readonly dialogRef = inject(MatDialogRef<DlgUpsertSessionComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  fb = inject(FormBuilder);

  form: FormGroup = new FormGroup({});

  title: string = 'Create Session';
  description: string = 'Enter Session Details';
  action: string = 'Create';

  isAssignable: boolean = true;

  faculty$: Observable<SchoolMember[] | null | undefined> = new Observable();
  lessons$: Observable<GradeLesson[] | null | undefined> = new Observable();
  grade: Grade | null = null;

  working$: Observable<boolean> = of(false);
  exit$: Subject<void> = new Subject();

  constructor() {
    const defaultTeacherId =
      this.data.defaultTeacherId ??
      this.data.session?.teacherId ??
      this.data.user?.uid ??
      'unassigned';
    const defaultTeacherName =
      this.data.defaultTeacherName ??
      this.data.session?.teacherName ??
      this.data.user?.name ??
      '';
    const defaultTeacherPhotoUrl =
      this.data.defaultTeacherPhotoUrl ??
      this.data.session?.teacherPhotoUrl ??
      this.data.user?.photoURL ??
      '';
    const defaultLessonId =
      this.data.defaultLessonId ??
      this.data.session?.lessonId ??
      'unassigned';
    const defaultLessonName =
      this.data.defaultLessonName ??
      this.data.session?.lessonName ??
      '';

    this.form = this.fb.group({
      sessionName: new FormControl(
        this.data.sessionName ??
          this.data.session?.sessionName ??
          moment().format('DD MMM yyyy'),
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ],
      ),

      startDate: new FormControl(
        this.data.startDate
          ? moment(this.data.startDate).format('YYYY-MM-DD')
          : this.data.session?.startDate
            ? moment(this.data.session.startDate.toDate()).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD'),
        [Validators.required],
      ),
      endDate: new FormControl(
        this.data.endDate
          ? moment(this.data.endDate).format('YYYY-MM-DD')
          : this.data.session?.endDate
            ? moment(this.data.session.endDate.toDate()).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD'),
        [Validators.required],
      ),
      startTime: new FormControl(
        this.data.startDate
          ? moment(this.data.startDate).format('HH:mm')
          : this.data.session?.startDate
            ? moment(this.data.session.startDate.toDate()).format('HH:mm')
            : moment().format('HH:mm'),
        [Validators.required],
      ),
      endTime: new FormControl(
        this.data.endDate
          ? moment(this.data.endDate).format('HH:mm')
          : this.data.session?.endDate
            ? moment(this.data.session.endDate.toDate()).format('HH:mm')
            : moment().add(45, 'minutes').format('HH:mm'),
        [Validators.required],
      ),
      teacherId: new FormControl(defaultTeacherId),
      teacherName: new FormControl(defaultTeacherName),
      teacherPhotoUrl: new FormControl(defaultTeacherPhotoUrl),
      lessonId: new FormControl(defaultLessonId),
      lessonName: new FormControl(defaultLessonName),
    });

    this.title =
      this.data.title ??
      (this.data.action === 'Update' ? 'Update Session' : 'Create Session');
    this.description =
      this.data.description ??
      (this.data.action === 'Update'
        ? 'Update Session Details'
        : 'Enter Session Details');
    this.action = this.data.action ?? 'Create';
    this.isAssignable = this.data.isAssignable ?? true;
    this.lessons$ = this.data.lessons$;
    this.grade = this.data.grade;
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit(): void {
    const groupId = this.data.groupId ?? this.data.session?.groupId;
    const gradeId =
      this.grade?.id ?? this.data.gradeId ?? this.data.session?.gradeId;
    const academicYear =
      this.grade?.gradeYear ??
      this.data.academicYear ??
      this.data.session?.academicYear;

    this.faculty$ =
      this.data.faculty$ ??
      (groupId
        ? this.store
            .select(
              memberSelector.getGroupMembersByType({
                memberType: [SchoolMemberType.teacher],
                groupId: groupId,
                status: [MembershipStatus.active],
              }),
            )
            .pipe(takeUntil(this.exit$))
        : of([]));

    this.lessons$ =
      this.data.lessons$ ??
      (gradeId && groupId
        ? this.store
            .select(
              gradeLessonsSelector.getGradeLessonsByGradeId({
                gradeId,
                groupId,
                academicYear,
              }),
            )
            .pipe(takeUntil(this.exit$))
        : of([]));

    this.actions$
      .pipe(
        ofType(sessionActions.create, sessionActions.update),
        takeUntil(this.exit$),
      )
      .subscribe(() => {
        this.working$ = of(true);
      });

    this.actions$
      .pipe(
        ofType(sessionActions.createSuccess, sessionActions.updateSuccess),
        takeUntil(this.exit$),
      )
      .subscribe(() => {
        this.logger.debug(`Session ${this.action.toLowerCase()}d successfully`);
        this.working$ = of(false);
        this.dialogRef.close(true);
      });

    this.actions$
      .pipe(
        ofType(sessionActions.createFailure, sessionActions.updateFailure),
        takeUntil(this.exit$),
      )
      .subscribe(({ error }) => {
        this.working$ = of(false);
        this.utilsService.onShowAlert(
          {
            title: `${this.action} Session Failed`,
            message: `Failed to ${this.action.toLowerCase()} session. Please try again. Error: ${error}`,
          },
          false,
        );
      });
  }

  onChangeOption(event: any): void {
    const value = event.target.value ?? 'unassigned';
    this.form.get('teacherId')?.setValue(value);
    if (value !== 'unassigned') {
      this.faculty$
        .pipe(
          take(1),
          map((faculty) => faculty?.find((f) => f.memberId === value) ?? null),
        )
        .subscribe((teacher) => {
          if (teacher) {
            this.form.get('teacherName')?.setValue(teacher.name ?? '');
            this.form.get('teacherPhotoUrl')?.setValue(teacher.photoURL ?? '');
          }
        });
    } else {
      this.form.get('teacherName')?.setValue('');
      this.form.get('teacherPhotoUrl')?.setValue('');
    }
  }

  onChangeLesson(event: any): void {
    const value = event.target.value ?? 'unassigned';
    this.form.get('lessonId')?.setValue(value);
    if (value !== 'unassigned') {
      this.lessons$
        .pipe(
          take(1),
          map((lessons) => lessons?.find((f) => f.id === value) ?? null),
        )
        .subscribe((lesson) => {
          if (lesson) {
            this.form.get('lessonName')?.setValue(lesson.lessonName ?? '');
          }
        });
    } else {
      this.form.get('lessonName')?.setValue('');
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    let teacherId = this.form.get('teacherId')?.value;
    let teacherName = this.form.get('teacherName')?.value;
    let teacherPhotoUrl = this.form.get('teacherPhotoUrl')?.value;
    let lessonId = this.form.get('lessonId')?.value;
    let lessonName = this.form.get('lessonName')?.value;

    if (teacherId === 'unassigned') {
      teacherId = '';
      teacherName = '';
      teacherPhotoUrl = '';
    }
    if (lessonId === 'unassigned') {
      lessonId = '';
      lessonName = '';
    }

    const formVal = this.form.value;
    const startMoment = moment(
      formVal.startDate + ' ' + formVal.startTime,
    );
    const endMoment = moment(
      formVal.endDate + ' ' + formVal.endTime,
    );

    const gradeId =
      this.grade?.id ?? this.data.gradeId ?? this.data.session?.gradeId;
    const gradeName =
      this.grade?.gradeName ??
      this.data.gradeName ??
      this.data.session?.gradeName ??
      '';
    const academicYear =
      this.grade?.gradeYear ??
      this.data.academicYear ??
      this.data.session?.academicYear;
    const groupId = this.data.groupId ?? this.data.session?.groupId;
    const sessionId = this.data.sessionId ?? this.data.session?.id;

    const session: Session = {
      ...(this.data.session ?? {}),
      sessionName: formVal.sessionName,
      sessionStatus:
        this.data.session?.sessionStatus ?? SessionStatus.active,
      gradeId: gradeId,
      groupId: groupId,
      gradeName: gradeName,
      academicYear: academicYear,
      startDate: FireTimestamp.fromDate(startMoment.toDate()),
      endDate: FireTimestamp.fromDate(endMoment.toDate()),
      teacherId: teacherId || (this.data.user?.uid ?? ''),
      teacherName: teacherName || (this.data.user?.name ?? ''),
      teacherPhotoUrl:
        teacherPhotoUrl || (this.data.user?.photoURL ?? ''),
      lessonId: lessonId || '',
      lessonName: lessonName || '',
    } as Session;

    const isUpdate = this.action === 'Update' || !!sessionId;

    if (groupId) {
      if (isUpdate && sessionId) {
        return this.store.dispatch(
          sessionActions.update({ groupId, sessionId, session }),
        );
      } else {
        return this.store.dispatch(
          sessionActions.create({ groupId, session }),
        );
      }
    }

    // Fallback: If no groupId was provided, close with form result
    this.dialogRef.close({
      ...formVal,
      startDate: startMoment,
      endDate: endMoment,
      teacherId,
      teacherName,
      teacherPhotoUrl,
      lessonId,
      lessonName,
      session,
    });
  }
}
